/**
 * The `wsm.fulfillment` shop-metadata entry: when the warehouse actually ships.
 *
 * The storefront reads this key to turn a carrier's transit DURATION into a
 * delivery date on the checkout (see wsm-storefront/src/lib/fulfillment.ts).
 * Before it existed the estimate counted transit days from "now", which
 * promised Monday delivery on a one-day service for an order placed Saturday
 * — the day the warehouse opens and ships it, not the day it lands.
 *
 * Shape is snake_case because that is what the storefront's parser reads, and
 * this file is the only place in the dashboard that knows the shape.
 */
export const FULFILLMENT_METADATA_KEY = "wsm.fulfillment";

export interface FulfillmentWindowPayload {
  is_active: boolean;
  cutoff_hour: number;
  cutoff_minute: number;
  time_zone: string;
  /** Weekday numbers the warehouse is CLOSED. 0 = Sunday .. 6 = Saturday. */
  non_shipping_days: number[];
  /** Extra closures as ISO "YYYY-MM-DD" — statutory holidays, shutdowns. */
  holidays: string[];
}

export const DEFAULT_WINDOW: FulfillmentWindowPayload = {
  is_active: false,
  cutoff_hour: 11,
  cutoff_minute: 0,
  // Filled from the browser by the view (see initialWindow). A blank default
  // was right while this was a free-text field, but the control is now a
  // labelled picker showing the live local time, so a visible, checkable guess
  // beats an empty box. The feature is off until the merchant enables it
  // anyway, so nothing applies before they have looked at this.
  time_zone: "",
  non_shipping_days: [0, 6],
  holidays: [],
};

/** Monday-first, because that is how a shipping week is read. */
export const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isIsoDate = (value: string): boolean =>
  ISO_DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

/**
 * Is this a zone name the browser can resolve? The storefront drops one it
 * cannot, so a typo here would silently disable the cut-off rather than
 * report anything — which is exactly why the form validates it on this side.
 */
export const isResolvableTimeZone = (zone: string): boolean => {
  if (!zone) return false;

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });

    return true;
  } catch {
    return false;
  }
};

/** Format an ISO date for display without dragging in a date library. */
export const formatIsoDate = (iso: string): string => {
  const parsed = new Date(`${iso}T12:00:00Z`);

  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString(undefined, {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
};

/** Read the stored entry, tolerating anything a hand-edit may have left. */
export function parseStoredWindow(raw: string | undefined): FulfillmentWindowPayload {
  if (!raw) return DEFAULT_WINDOW;

  try {
    const parsed = JSON.parse(raw) as Partial<FulfillmentWindowPayload> | null;

    if (!parsed || typeof parsed !== "object") return DEFAULT_WINDOW;

    const days = Array.isArray(parsed.non_shipping_days)
      ? parsed.non_shipping_days.filter(d => Number.isInteger(d) && d >= 0 && d <= 6)
      : DEFAULT_WINDOW.non_shipping_days;
    const holidays = Array.isArray(parsed.holidays)
      ? parsed.holidays.filter(h => typeof h === "string" && isIsoDate(h))
      : [];

    return {
      is_active: parsed.is_active === true,
      cutoff_hour: clampInt(parsed.cutoff_hour, 0, 23, DEFAULT_WINDOW.cutoff_hour),
      cutoff_minute: clampInt(parsed.cutoff_minute, 0, 59, DEFAULT_WINDOW.cutoff_minute),
      time_zone: typeof parsed.time_zone === "string" ? parsed.time_zone : "",
      non_shipping_days: [...new Set(days)].sort(),
      holidays: [...new Set(holidays)].sort(),
    };
  } catch {
    return DEFAULT_WINDOW;
  }
}

function clampInt(value: unknown, lo: number, hi: number, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value >= lo && value <= hi
    ? value
    : fallback;
}

// --- cut-off time -----------------------------------------------------------
//
// Stored as separate hour/minute integers because that is what the storefront
// parses, but edited as ONE value: two number inputs made a merchant do 24-hour
// arithmetic to say "11am", and got them a spinner for a field with 24 valid
// values.

/** {11, 0} -> "11:00", the value an <input type="time"> wants. */
export const toTimeValue = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

/** "11:00" -> {hour: 11, minute: 0}. Null for anything else, so a half-typed
 *  or cleared field never lands in the payload as a real cut-off. */
export function fromTimeValue(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());

  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return { hour, minute };
}

/** "11:00 AM" — for restating the rule in words under the controls. */
export function formatTimeOfDay(hour: number, minute: number): string {
  const d = new Date(2000, 0, 1, hour, minute);

  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// --- time zone --------------------------------------------------------------
//
// A curated list, not Intl.supportedValuesOf(): that returns ~400 zones, and a
// merchant picking their warehouse does not want to scroll past Antarctica.
// These cover the fleet (US + Canada, with Mexico and the territories that
// actually ship). A configured zone outside the list is never dropped -- see
// timeZoneOptions.
export const COMMON_TIME_ZONES = [
  "America/St_Johns",
  "America/Halifax",
  "America/New_York",
  "America/Toronto",
  "America/Chicago",
  "America/Winnipeg",
  "America/Regina",
  "America/Denver",
  "America/Edmonton",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Vancouver",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Mexico_City",
  "America/Puerto_Rico",
  "UTC",
];

/** The zone this browser is in, or "" when it cannot be determined. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

/** "Toronto (EDT)" — city plus whatever abbreviation is current, so the list
 *  reads like places rather than database identifiers. */
export function zoneLabel(zone: string): string {
  const city = (zone.split("/").pop() ?? zone).replace(/_/g, " ");

  if (!isResolvableTimeZone(zone)) return zone;

  try {
    const abbr = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
      .formatToParts(new Date())
      .find(part => part.type === "timeZoneName")?.value;

    return abbr && abbr !== city ? `${city} (${abbr})` : city;
  } catch {
    return city;
  }
}

/**
 * Options for the picker: the curated list, plus the browser's own zone and any
 * already-stored zone that is not in it.
 *
 * That last part is the important one. Rendering a Select whose value is absent
 * from its options silently shows nothing selected, and the first save would
 * then write a DIFFERENT zone than the merchant had -- quietly moving every
 * delivery date on the store.
 */
export function timeZoneOptions(
  configured: string,
  detected: string = browserTimeZone(),
): Array<{ label: string; value: string }> {
  const zones = [...COMMON_TIME_ZONES];

  for (const extra of [detected, configured]) {
    if (extra && !zones.includes(extra)) zones.unshift(extra);
  }

  return zones.map(zone => ({ label: zoneLabel(zone), value: zone }));
}

/** "11:42 PM" in the given zone, or "" if it cannot be read. Shown live under
 *  the picker: the whole question a merchant has is "is this my clock?". */
export function nowInZone(zone: string): string {
  if (!isResolvableTimeZone(zone)) return "";

  try {
    return new Date().toLocaleTimeString(undefined, {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
